import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';

export type ViewTabListProps = Omit<React.ComponentProps<'div'>, 'style'>;

/*
 * View tabs: for switching between modes of one object — the meeting page's
 * Agenda | Verslag. The other half of the pair in tabs.tsx, which underlines
 * instead and is for content sections of a page. Content sections underline;
 * object views pill. Never both on one page.
 *
 * List and tab are a pair rather than a `shape` variant on the existing ones
 * because the two halves have to agree: pills under a section tablist's bottom
 * border is a shape nobody meant to draw, and a variant threaded onto only one
 * of the two elements draws it silently.
 */
export function ViewTabList({ className, ...props }: ViewTabListProps) {
  return (
    <div
      data-slot='view-tab-list'
      role='tablist'
      /*
       * Never wraps. The track is one bordered control, and wrapping stacks the
       * segments into a tall pill on a phone — two rows inside a single rounded
       * border, which reads as a rendering fault rather than a switch. Narrow
       * screens shrink the segments and let their labels run onto a second line
       * instead, which is what keeps the control one line high everywhere.
       */
      className={cn(
        'inline-flex w-fit items-center gap-0.5 rounded-full border border-line bg-surface p-0.5',
        className,
      )}
      {...props}
    />
  );
}

/*
 * One bordered track holding borderless segments; the active segment fills
 * navy. The fill inverts rather than leaning on a colour accent, so the
 * distinction survives at any contrast setting — the green icon on the active
 * segment is decoration on top of that, never the signal itself.
 */
export const viewTabVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.75 rounded-full px-4.5 py-1.5 text-center text-base font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
    'disabled:opacity-40',
  ],
  {
    variants: {
      active: {
        true: 'bg-primary text-white ring-1 ring-selected-edge [&_svg]:text-accent',
        false: 'text-ink-secondary hover:text-ink',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type ViewTabVariants = VariantProps<typeof viewTabVariants>;

export interface ViewTabProps
  extends Omit<React.ComponentProps<'button'>, 'style' | 'type'>, ViewTabVariants {
  type?: ButtonType;
}

/*
 * `aria-selected` follows `active` rather than being asked for separately: a tab
 * that looks active and does not say so is a tab screen readers cannot find.
 */
export function ViewTab({ className, active, type = 'button', ...props }: ViewTabProps) {
  return (
    <button
      data-slot='view-tab'
      role='tab'
      type={type}
      aria-selected={Boolean(active)}
      className={cn(viewTabVariants({ active }), className)}
      {...props}
    />
  );
}
